import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import PowloLayout from "@/components/powlo/PowloLayout";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { TRADING_NAME } from "@/constants/company";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Paddle?: any;
  }
}

/**
 * Loads the Paddle.js v2 script tag once. Returns a promise that resolves
 * with window.Paddle when ready, or rejects on timeout / load failure.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadPaddleJs(): Promise<any> {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.Paddle) return Promise.resolve(window.Paddle);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-paddle-v2]',
    ) as HTMLScriptElement | null;

    const onReady = () => {
      if (window.Paddle) resolve(window.Paddle);
      else reject(new Error("Paddle.js loaded but window.Paddle is undefined"));
    };

    if (existing) {
      if (window.Paddle) resolve(window.Paddle);
      else existing.addEventListener("load", onReady);
      existing.addEventListener(
        "error",
        () => reject(new Error("Paddle.js script failed to load")),
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.async = true;
    script.defer = true;
    script.dataset.paddleV2 = "true";
    script.addEventListener("load", onReady);
    script.addEventListener(
      "error",
      () => reject(new Error("Paddle.js script failed to load")),
    );
    document.head.appendChild(script);

    setTimeout(() => {
      if (!window.Paddle)
        reject(new Error("Paddle.js load timed out after 8s"));
    }, 8000);
  });
}

function isStripeSessionId(id: string): boolean {
  return id.startsWith("cs_");
}

/**
 * Public payment link landing page.
 *
 * Accepts the session/transaction id in:
 *   /pay/:sessionId          (clean shareable URL)
 *   /pay?session_id=cs_...   (Stripe Checkout redirect format)
 *   /pay?_ptxn=txn_...       (Paddle's native redirect format — kept for rollback)
 */
export default function Pay() {
  const [, params] = useRoute<{ transactionId?: string }>(
    "/pay/:transactionId",
  );
  const [location] = useLocation();
  const [status, setStatus] = useState<
    "loading" | "opening" | "opened" | "error" | "no-id"
  >("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [provider, setProvider] = useState<"stripe" | "paddle" | null>(null);

  const query = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const pathId = params?.transactionId ?? "";
  const stripeSessionId =
    query.get("session_id") ??
    (isStripeSessionId(pathId) ? pathId : "");
  const paddleTransactionId =
    query.get("_ptxn") ??
    (pathId && !isStripeSessionId(pathId) ? pathId : "");

  const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as
    | string
    | undefined;
  const paddleToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as
    | string
    | undefined;
  const paddleEnv = (import.meta.env.VITE_PADDLE_ENV as
    | "sandbox"
    | "production"
    | undefined) ?? "sandbox";

  useEffect(() => {
    if (stripeSessionId) {
      setProvider("stripe");
      if (!stripePublishableKey) {
        setStatus("error");
        setErrorMsg(
          "Payment is not configured on this site (missing VITE_STRIPE_PUBLISHABLE_KEY).",
        );
        return;
      }

      let cancelled = false;
      setStatus("opening");

      loadStripe(stripePublishableKey)
        .then((stripe) => {
          if (cancelled) return;
          if (!stripe) {
            setStatus("error");
            setErrorMsg("Stripe.js failed to initialise.");
            return;
          }
          return stripe.redirectToCheckout({ sessionId: stripeSessionId });
        })
        .then((result) => {
          if (cancelled || !result) return;
          if (result.error) {
            setStatus("error");
            setErrorMsg(result.error.message ?? "Stripe checkout redirect failed");
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setStatus("error");
          setErrorMsg(err instanceof Error ? err.message : String(err));
        });

      return () => {
        cancelled = true;
      };
    }

    if (!paddleTransactionId) {
      setStatus("no-id");
      return;
    }

    setProvider("paddle");
    if (!paddleToken) {
      setStatus("error");
      setErrorMsg(
        "Payment is not configured on this site (missing VITE_PADDLE_CLIENT_TOKEN).",
      );
      return;
    }

    let cancelled = false;
    setStatus("loading");

    loadPaddleJs()
      .then((Paddle) => {
        if (cancelled) return;
        try {
          Paddle.Environment.set(paddleEnv);
          Paddle.Initialize({ token: paddleToken });

          window.addEventListener("paddle:checkout:closed", () => {
            if (!cancelled) setStatus("opened");
          });
          window.addEventListener(
            "paddle:checkout:completed",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (e: any) => {
              if (cancelled) return;
              console.log("Paddle checkout completed", e?.detail);
            },
          );

          setStatus("opening");
          Paddle.Checkout.open({
            transactionId: paddleTransactionId,
            settings: {
              displayMode: "overlay",
              theme: "light",
            },
          });
          setStatus("opened");
        } catch (err) {
          setStatus("error");
          setErrorMsg(
            err instanceof Error
              ? err.message
              : "Paddle.Checkout.open failed",
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [
    stripeSessionId,
    stripePublishableKey,
    paddleTransactionId,
    paddleToken,
    paddleEnv,
    location,
  ]);

  const statusCopy = (() => {
    switch (status) {
      case "loading":
        return "Loading secure checkout…";
      case "opening":
        return provider === "stripe"
          ? "Redirecting to secure checkout…"
          : "Opening checkout…";
      case "opened":
        return "Checkout is open. Complete payment in the overlay to continue.";
      case "no-id":
        return "Missing transaction id. Open the link from your email or contact us if this keeps appearing.";
      case "error":
        return errorMsg || "Something went wrong opening the checkout.";
    }
  })();

  return (
    <PowloLayout>
      <div className="powlo-on-cream">
      <Seo
        title="Secure payment"
        description={`Complete your payment securely with ${TRADING_NAME}.`}
        path="/pay"
      />

      <section className="container max-w-xl mx-auto py-20 px-6">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
          {status === "loading" || status === "opening" ? (
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#29ffa9]" />
          ) : status === "error" ? (
            <AlertTriangle className="mx-auto h-10 w-10 text-amber-400" />
          ) : null}

          <h1 className="mt-6 text-2xl font-semibold text-white">
            {status === "opened"
              ? "Secure payment"
              : status === "error"
                ? "We couldn't open the checkout"
                : "Secure payment"}
          </h1>

          <p className="mt-3 text-white/70 leading-relaxed">{statusCopy}</p>

          {status === "no-id" || status === "error" ? (
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                className="bg-[#29ffa9] text-[#050d1a] hover:bg-[#29ffa9]/90"
              >
                <a href="/contact">
                  Contact support
                </a>
              </Button>
              <Button
                variant="outline"
                asChild
                className="border-white/15 text-white/70 hover:text-white hover:bg-white/5"
              >
                <a href="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to home
                </a>
              </Button>
            </div>
          ) : null}

          <p className="mt-8 text-xs text-white/40">
            {provider === "paddle"
              ? "Payments are processed by Paddle.com — Powlo never sees your card details."
              : "Payments are processed securely — Powlo never sees your card details."}
          </p>
        </div>
      </section>
      </div>
    </PowloLayout>
  );
}
